import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import emailService, { EmailMailbox } from '../services/emailService';
import { Tooltip } from 'react-tooltip';
import MailboxBulkUpload from '../components/email/MailboxBulkUpload';

// Type for mailbox form
interface MailboxForm {
  email: string;
  name: string;
  daily_limit: number;
  smtp_host: string;
  smtp_port: number;
  smtp_secure: boolean;
  smtp_username: string;
  smtp_password: string;
  imap_host: string;
  imap_port: number;
  imap_username: string;
  imap_password: string;
}

// Type for warmup status
interface WarmupStatus {
  id: string;
  mailbox_id: string;
  user_id: string;
  is_active: boolean;
  daily_limit: number;
  current_daily_volume: number;
  warmup_started_at: string;
  last_warmup_at: string | null;
  next_scheduled_at: string | null;
  reputation_score: number;
  last_error: string | null;
  config: WarmupConfig;
  created_at: string;
  updated_at: string;
}

// Type for warmup config
interface WarmupConfig {
  increase_rate: number; // Percentage to increase daily sends
  increase_days: number; // How many days between increases
  max_daily: number; // Maximum daily emails to send
}

// Type for warmup form
interface WarmupForm {
  increase_rate: number;
  increase_days: number;
  max_daily: number;
}

// Extended mailbox type with warmup status
interface MailboxWithWarmup extends EmailMailbox {
  warmupStatus?: WarmupStatus | null;
}

const EmailMailboxes: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [mailboxes, setMailboxes] = useState<MailboxWithWarmup[]>([]);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState<boolean>(false);
  const [showWarmupModal, setShowWarmupModal] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isWarmupLoading, setIsWarmupLoading] = useState<Record<string, boolean>>({});
  const [currentMailboxId, setCurrentMailboxId] = useState<string | null>(null);
  const [encryptionConfigured, setEncryptionConfigured] = useState<boolean | null>(null);
  const [formData, setFormData] = useState<MailboxForm>({
    email: '',
    name: '',
    daily_limit: 100,
    smtp_host: '',
    smtp_port: 587,
    smtp_secure: true,
    smtp_username: '',
    smtp_password: '',
    imap_host: '',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
  });
  const [warmupForm, setWarmupForm] = useState<WarmupForm>({
    increase_rate: 0,
    increase_days: 9999,
    max_daily: 20
  });
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchMailboxes();
      checkEncryptionStatus();
    }
  }, [user]);

  const fetchMailboxes = async () => {
    setIsLoading(true);
    try {
      const data = await emailService.getMailboxes();
      
      // Get warmup status for each mailbox
      const mailboxesWithWarmup = await Promise.all(
        data.map(async (mailbox) => {
          if (!mailbox.id) return { ...mailbox, warmupStatus: null };
          
          try {
            const warmupStatus = await emailService.getMailboxWarmupStatus(mailbox.id);
            return { ...mailbox, warmupStatus };
          } catch (err) {
            console.error(`Error fetching warmup status for mailbox ${mailbox.id}:`, err);
            return { ...mailbox, warmupStatus: null };
          }
        })
      );
      
      setMailboxes(mailboxesWithWarmup);
    } catch (error) {
      console.error('Error fetching mailboxes:', error);
      toast.error('Failed to load mailboxes');
    } finally {
      setIsLoading(false);
    }
  };

  const checkEncryptionStatus = async () => {
    try {
      const configured = await emailService.getEncryptionStatus();
      setEncryptionConfigured(configured);
    } catch (error) {
      console.error('Error checking encryption status:', error);
      setEncryptionConfigured(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' 
        ? (e.target as HTMLInputElement).checked 
        : type === 'number' 
          ? parseInt(value, 10) 
          : value
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const validateForm = () => {
    const requiredFields = [
      'email', 'name', 'smtp_host', 'smtp_port', 
      'smtp_username', 'smtp_password'
    ];
    
    for (const field of requiredFields) {
      if (!formData[field as keyof MailboxForm]) {
        toast.error(`${field.replace('_', ' ')} is required`);
        return false;
      }
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    
    return true;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) return;
    
    setTestStatus('testing');
    try {
      // Create a temporary mailbox object for testing
      const testMailbox: EmailMailbox = {
        email: formData.email,
        name: formData.name,
        smtp_host: formData.smtp_host,
        smtp_port: formData.smtp_port,
        smtp_secure: formData.smtp_secure,
        smtp_username: formData.smtp_username,
        smtp_password: formData.smtp_password,
        imap_host: formData.imap_host,
        imap_port: formData.imap_port,
        imap_username: formData.imap_username,
        imap_password: formData.imap_password,
        daily_limit: formData.daily_limit,
        status: 'active'
      };

      const success = await emailService.testMailboxConnection(testMailbox);
      
      if (success) {
        setTestStatus('success');
        toast.success('Connection test successful!');
      } else {
        setTestStatus('error');
        toast.error('Failed to connect to mail server');
      }
    } catch (error) {
      setTestStatus('error');
      toast.error('Failed to connect to mail server');
      console.error('Connection test error:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      // Create a mailbox object with all the form data
      const mailboxData: EmailMailbox = {
        email: formData.email,
        name: formData.name,
        daily_limit: formData.daily_limit,
        smtp_host: formData.smtp_host,
        smtp_port: formData.smtp_port,
        smtp_secure: formData.smtp_secure,
        smtp_username: formData.smtp_username,
        smtp_password: formData.smtp_password, // Password is sent securely to backend
        imap_host: formData.imap_host,
        imap_port: formData.imap_port,
        imap_username: formData.imap_username,
        imap_password: formData.imap_password,
        status: 'active'
      };

      await emailService.createMailbox(mailboxData);
      
      toast.success('Mailbox added successfully!');
      setShowAddModal(false);
      fetchMailboxes();
      
      // Reset form
      setFormData({
        email: '',
        name: '',
        daily_limit: 100,
        smtp_host: '',
        smtp_port: 587,
        smtp_secure: true,
        smtp_username: '',
        smtp_password: '',
        imap_host: '',
        imap_port: 993,
        imap_username: '',
        imap_password: '',
      });
    } catch (error) {
      console.error('Error adding mailbox:', error);
      toast.error('Failed to add mailbox');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMailboxStatus = async (id: string, currentStatus: 'active' | 'paused' | 'error') => {
    if (!id) return;
    
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    
    try {
      await emailService.updateMailbox(id, { status: newStatus });
      
      // Update the local state
      setMailboxes(mailboxes.map(mailbox => 
        mailbox.id === id ? { ...mailbox, status: newStatus } : mailbox
      ));
      
      toast.success(`Mailbox ${newStatus === 'active' ? 'activated' : 'paused'}`);
    } catch (error) {
      console.error('Error updating mailbox status:', error);
      toast.error('Failed to update mailbox status');
    }
  };

  const handleDeleteMailbox = async (id: string | undefined) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this mailbox?')) return;
    
    try {
      await emailService.deleteMailbox(id);
      
      // Update the local state
      setMailboxes(mailboxes.filter(mailbox => mailbox.id !== id));
      toast.success('Mailbox deleted');
    } catch (error) {
      console.error('Error deleting mailbox:', error);
      toast.error('Failed to delete mailbox');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Format date in a readable way
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Handle warmup toggle
  const handleWarmupToggle = async (mailbox: MailboxWithWarmup) => {
    if (!mailbox.id) return;
    
    // Set loading state for this mailbox
    setIsWarmupLoading(prev => ({ ...prev, [mailbox.id!]: true }));
    
    try {
      if (mailbox.warmupStatus?.is_active) {
        // Disable warmup
        const result = await emailService.disableWarmup(mailbox.id);
        
        // Update the mailbox in state
        setMailboxes(prevMailboxes => 
          prevMailboxes.map(m => 
            m.id === mailbox.id ? { ...m, warmupStatus: result } : m
          )
        );
        
        toast.success('Email warmup disabled');
      } else {
        // Enable warmup with system-managed locked schedule
        const result = await emailService.enableWarmup(mailbox.id);
        
        // Update the mailbox in state
        setMailboxes(prevMailboxes => 
          prevMailboxes.map(m => 
            m.id === mailbox.id ? { ...m, warmupStatus: result } : m
          )
        );
        
        toast.success('Email warmup enabled');
      }
    } catch (error) {
      console.error('Error toggling warmup status:', error);
      toast.error('Failed to update warmup status');
    } finally {
      setIsWarmupLoading(prev => ({ ...prev, [mailbox.id!]: false }));
    }
  };
  
  // Handle saving warmup configuration
  const handleSaveWarmupConfig = async () => {
    if (!currentMailboxId) return;
    
    setIsSubmitting(true);
    
    try {
      // Enable warmup with system-managed locked schedule (no user-editable config)
      const result = await emailService.enableWarmup(currentMailboxId);
      
      // Update the mailbox in state
      setMailboxes(prevMailboxes => 
        prevMailboxes.map(m => 
          m.id === currentMailboxId ? { ...m, warmupStatus: result } : m
        )
      );
      
      setShowWarmupModal(false);
      toast.success('Email warmup configured and enabled');
    } catch (error) {
      console.error('Error saving warmup configuration:', error);
      toast.error('Failed to save warmup configuration');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle warmup form input changes
  const handleWarmupInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setWarmupForm({
      ...warmupForm,
      [name]: parseInt(value, 10)
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading mailboxes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {encryptionConfigured === false && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm px-4 py-3 rounded">
          <p className="font-semibold">Security note</p>
          <p className="mt-1">
            SMTP/IMAP passwords are currently encrypted with an ephemeral key. Ask your administrator to set
            <code className="mx-1">MAILBOX_ENCRYPTION_KEY</code> in the backend environment so mailbox credentials remain decryptable and secure across restarts.
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Email Mailboxes</h1>
        <div className="flex gap-3">
          <button
            onClick={() => setShowBulkUploadModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Bulk Import
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
          >
            Add Mailbox
          </button>
        </div>
      </div>

      {mailboxes.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-6xl mb-4">📬</div>
          <h2 className="text-2xl font-semibold mb-2">No Mailboxes Found</h2>
          <p className="text-gray-600 mb-6">
            You haven't added any mailboxes yet. Add a mailbox to start sending cold emails.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
          >
            Add Your First Mailbox
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email / Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Daily Limit
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SMTP / IMAP
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Warmup
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mailboxes.map((mailbox) => (
                <tr key={mailbox.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{mailbox.email}</div>
                    <div className="text-sm text-gray-500">{mailbox.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {mailbox.emails_sent_today ?? 0} / {mailbox.daily_limit}
                    </div>
                    <div className="w-24 h-2 bg-gray-200 rounded-full mt-1">
                      <div 
                        className="h-2 bg-blue-600 rounded-full" 
                        style={{ width: `${(((mailbox.emails_sent_today ?? 0) / (mailbox.daily_limit || 1)) * 100)}%` }}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(mailbox.status)}`}>
                      {mailbox.status.charAt(0).toUpperCase() + mailbox.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">SMTP: {mailbox.smtp_host}:{mailbox.smtp_port}</div>
                    {mailbox.imap_host && (
                      <div className="text-sm text-gray-500">IMAP: {mailbox.imap_host}:{mailbox.imap_port}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        {mailbox.warmupStatus ? (
                          <div>
                            <span 
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${mailbox.warmupStatus.is_active ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}
                              data-tooltip-id={`warmup-tooltip-${mailbox.id}`}
                              data-tooltip-content={`Started: ${formatDate(mailbox.warmupStatus.warmup_started_at)}<br/>Last run: ${formatDate(mailbox.warmupStatus.last_warmup_at)}<br/>Next run: ${formatDate(mailbox.warmupStatus.next_scheduled_at)}`}
                            >
                              {mailbox.warmupStatus.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <Tooltip id={`warmup-tooltip-${mailbox.id}`} place="top" html={true} />
                            {mailbox.warmupStatus.is_active && (
                              <div className="mt-1">
                                <div className="text-xs text-gray-500">
                                  Volume: {mailbox.warmupStatus.current_daily_volume} emails/day
                                </div>
                                <div className="text-xs text-gray-500">
                                  Score: {Math.round(mailbox.warmupStatus.reputation_score * 100)}%
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">Not configured</span>
                        )}
                      </div>
                      <div>
                        <button
                          onClick={() => handleWarmupToggle(mailbox)}
                          disabled={isWarmupLoading[mailbox.id || '']}
                          className={`px-3 py-1 text-xs rounded ${mailbox.warmupStatus?.is_active ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                        >
                          {isWarmupLoading[mailbox.id || ''] ? (
                            <span className="inline-block w-4 h-4 border-2 border-gray-500 border-t-blue-500 rounded-full animate-spin"></span>
                          ) : mailbox.warmupStatus?.is_active ? (
                            'Disable'
                          ) : (
                            'Enable'
                          )}
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button 
                      onClick={() => mailbox.id && toggleMailboxStatus(mailbox.id, mailbox.status)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      {mailbox.status === 'active' ? 'Pause' : 'Activate'}
                    </button>
                    <Link 
                      to={mailbox.id ? `/email/mailboxes/${mailbox.id}` : `/email/mailboxes`}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Edit
                    </Link>
                    <button 
                      onClick={() => handleDeleteMailbox(mailbox.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Warmup Configuration Modal */}
      {showWarmupModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Configure Email Warmup</h3>
                <button 
                  onClick={() => setShowWarmupModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  &times;
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <div className="space-y-4 mb-4">
                <p className="text-sm text-gray-600">
                  Email warmup is managed automatically to protect your domain. Explanatum sends up to 20 warmup emails per day from this
                  mailbox and keeps those messages out of your main inbox. Settings are locked for safety; you can only enable or disable
                  warmup.
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="increase_rate" className="block text-sm font-medium text-gray-700 mb-1">
                    Increase Rate (% per period)
                  </label>
                  <input
                    type="number"
                    name="increase_rate"
                    id="increase_rate"
                    min="5"
                    max="30"
                    value={warmupForm.increase_rate}
                    onChange={handleWarmupInputChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                    disabled
                  />
                  <p className="mt-1 text-xs text-gray-500">Managed automatically by the system.</p>
                </div>
                
                <div>
                  <label htmlFor="increase_days" className="block text-sm font-medium text-gray-700 mb-1">
                    Increase Period (days)
                  </label>
                  <input
                    type="number"
                    name="increase_days"
                    id="increase_days"
                    min="1"
                    max="7"
                    value={warmupForm.increase_days}
                    onChange={handleWarmupInputChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                    disabled
                  />
                  <p className="mt-1 text-xs text-gray-500">Managed automatically by the system.</p>
                </div>
                
                <div>
                  <label htmlFor="max_daily" className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Daily Volume
                  </label>
                  <input
                    type="number"
                    name="max_daily"
                    id="max_daily"
                    min="10"
                    max="100"
                    value={warmupForm.max_daily}
                    onChange={handleWarmupInputChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                    disabled
                  />
                  <p className="mt-1 text-xs text-gray-500">Locked at 20 warmup emails per day for mailbox safety.</p>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowWarmupModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveWarmupConfig}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none"
              >
                {isSubmitting ? 'Saving...' : 'Save & Enable'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Mailbox Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Add New Mailbox</h3>
                <button 
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  &times;
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4">
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-semibold mb-3">General Settings</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address*
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          placeholder="your@email.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                          Mailbox Name*
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          placeholder="e.g., Work Email"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="daily_limit" className="block text-sm font-medium text-gray-700 mb-1">
                          Daily Email Limit*
                        </label>
                        <input
                          type="number"
                          name="daily_limit"
                          id="daily_limit"
                          min="1"
                          max="500"
                          value={formData.daily_limit}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Maximum number of emails to send per day
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-md font-semibold mb-3">SMTP Settings (Outgoing Mail)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="smtp_host" className="block text-sm font-medium text-gray-700 mb-1">
                          SMTP Host*
                        </label>
                        <input
                          type="text"
                          name="smtp_host"
                          id="smtp_host"
                          placeholder="e.g., smtp.ionos.com"
                          value={formData.smtp_host}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="smtp_port" className="block text-sm font-medium text-gray-700 mb-1">
                          SMTP Port*
                        </label>
                        <input
                          type="number"
                          name="smtp_port"
                          id="smtp_port"
                          placeholder="587"
                          value={formData.smtp_port}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="smtp_username" className="block text-sm font-medium text-gray-700 mb-1">
                          SMTP Username*
                        </label>
                        <input
                          type="text"
                          name="smtp_username"
                          id="smtp_username"
                          placeholder="Usually your email address"
                          value={formData.smtp_username}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="smtp_password" className="block text-sm font-medium text-gray-700 mb-1">
                          SMTP Password*
                        </label>
                        <input
                          type="password"
                          name="smtp_password"
                          id="smtp_password"
                          placeholder="Your email password"
                          value={formData.smtp_password}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="smtp_secure"
                            checked={formData.smtp_secure}
                            onChange={handleCheckboxChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Use SSL/TLS</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-md font-semibold mb-3">IMAP Settings (Optional - For Tracking Replies)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="imap_host" className="block text-sm font-medium text-gray-700 mb-1">
                          IMAP Host
                        </label>
                        <input
                          type="text"
                          name="imap_host"
                          id="imap_host"
                          placeholder="e.g., imap.ionos.com"
                          value={formData.imap_host}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label htmlFor="imap_port" className="block text-sm font-medium text-gray-700 mb-1">
                          IMAP Port
                        </label>
                        <input
                          type="number"
                          name="imap_port"
                          id="imap_port"
                          placeholder="993"
                          value={formData.imap_port}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label htmlFor="imap_username" className="block text-sm font-medium text-gray-700 mb-1">
                          IMAP Username
                        </label>
                        <input
                          type="text"
                          name="imap_username"
                          id="imap_username"
                          placeholder="Usually your email address"
                          value={formData.imap_username}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label htmlFor="imap_password" className="block text-sm font-medium text-gray-700 mb-1">
                          IMAP Password
                        </label>
                        <input
                          type="password"
                          name="imap_password"
                          id="imap_password"
                          placeholder="Your email password"
                          value={formData.imap_password}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testStatus === 'testing' || isSubmitting}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  {testStatus === 'testing' ? (
                    <>
                      <span className="animate-spin mr-2">⟳</span>
                      Testing...
                    </>
                  ) : testStatus === 'success' ? (
                    <>
                      <span className="text-green-500 mr-2">✓</span>
                      Connection Successful
                    </>
                  ) : testStatus === 'error' ? (
                    <>
                      <span className="text-red-500 mr-2">✗</span>
                      Connection Failed
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </button>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
                  >
                    {isSubmitting ? 'Adding...' : 'Add Mailbox'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <MailboxBulkUpload
          onComplete={() => {
            setShowBulkUploadModal(false);
            fetchMailboxes();
          }}
          onCancel={() => setShowBulkUploadModal(false)}
        />
      )}
    </div>
  );
};

export default EmailMailboxes;
